// src/components/InteractiveFace.js
import React, { useState, useEffect } from 'react';
import { Draggable } from '@hello-pangea/dnd';
import { Paper, Box, IconButton, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';

const API_URL = '/api';  //'http://localhost:8000';

const InteractiveFace = ({ face, index, onRemoveFace, size = '80px' }) => {
  // --- 1. ADD STATE to hold the final image source URL ---
  const [imageSrc, setImageSrc] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // --- 2. ADD an EFFECT to determine and fetch the image source ---
  useEffect(() => {
    let objectUrl = null; // To keep track of the blob URL for cleanup

    const getImageUrl = async () => {
      setIsLoading(true);
      if (face.preview_url) {
        // Case 1: This is a temporary face with a preview URL. Use it directly.
        setImageSrc(face.preview_url);
        setIsLoading(false);
      } else {
        // Case 2: This is a saved face from the library. Fetch it from the secure endpoint.
        try {
          const response = await axios.get(`${API_URL}/faces/image/${face.id}`, {
            responseType: 'blob', // Important: we want the raw image data
          });
          objectUrl = URL.createObjectURL(response.data);
          setImageSrc(objectUrl);
        } catch (error) {
          console.error("Failed to load face image:", error);
          // You could set a placeholder for broken images here
          setImageSrc(null);
        } finally {
          setIsLoading(false);
        }
      }
    };

    getImageUrl();

    // --- 3. ADD a cleanup function ---
    // This is crucial to prevent memory leaks by revoking the temporary blob URL
    // when the component is unmounted.
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [face.id, face.preview_url]); // Rerun this effect if the face changes

  return (
    <Draggable draggableId={String(face.id)} index={index}>
      {(provided, snapshot) => (
        <Paper
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          sx={{
            p: 0.5,
            m: 0.5,
            userSelect: 'none',
            position: 'relative',
            border: snapshot.isDragging ? '2px solid' : '1px solid',
            borderColor: snapshot.isDragging ? 'primary.main' : 'divider',
            width: size,
            height: size,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden', // Hide overflow
            '&:hover .remove-icon': {
              opacity: 1,
            }
          }}
          elevation={2}
        >
          {isLoading ? (
            <CircularProgress size={20} />
          ) : imageSrc ? (
            <Box
              component="img"
              // --- 4. USE the state variable for the src ---
              src={imageSrc}
              alt={`Face ${face.id}`}
              sx={{
                width: `calc(${size} - 8px)`, 
                height: `calc(${size} - 8px)`,
                objectFit: 'cover',
                borderRadius: 1,
                display: 'block',
              }}
            />
          ) : (
            // Optional: Placeholder for a broken image
            <Box sx={{fontSize: '10px', textAlign: 'center', p: 1}}>Error</Box>
          )}

          <IconButton
            size="small"
            onClick={() => onRemoveFace(face.id)}
            className="remove-icon"
            sx={{
              position: 'absolute',
              top: 2,
              right: 2,
              opacity: 0,
              transition: 'opacity 0.2s',
              backgroundColor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.7)',
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Paper>
      )}
    </Draggable>
  );
};

export default InteractiveFace;
